import React, { useState, useEffect, useRef } from 'react';
import { useCurrentlyReading } from '../../hooks/useCurrentlyReading';
import { useSelector } from 'react-redux';
import { serverTimestamp } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';

const BookCoverPage = ({
  link,
  imageSrc,
  bookLabel,
  endPage,
  onToggle,
  isReading,
}) => {
  const { currentlyReading } = useCurrentlyReading();
  const [isIntersected, setIsIntersected] = useState(false);
  const imgRef = useRef(null);
  const user = useSelector((state) => state.userReducer);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIntersected(true);
        observer.unobserve(entry.target);
      }
    });

    const currentImgRef = imgRef.current;
    if (currentImgRef) {
      observer.observe(currentImgRef);
    }

    return () => {
      if (currentImgRef) {
        observer.unobserve(currentImgRef);
      }
    };
  }, []);

  const currentlyReadingBook = currentlyReading.find((b) => b.link === link);
  const startPage =
    Number(typeof link === 'string' ? link.split('/').pop() : 0) || 0;

  const getReadingLink = () => {
    if (currentlyReadingBook && typeof link === 'string') {
      const linkPrefix = link.substring(0, link.lastIndexOf('/'));
      return `${linkPrefix}/${currentlyReadingBook.pageNo}?status=reading`;
    }
    return link || '';
  };

  const calculateProgress = () => {
    if (!currentlyReadingBook) return 0;
    const {
      pageNo,
      startPage: bookStart,
      endPage: bookEnd,
    } = currentlyReadingBook;
    const total = bookEnd - bookStart;
    if (total <= 0) return 100;
    const progress = pageNo - bookStart;
    return Math.round((progress / total) * 100);
  };

  return (
    <Link
      to={getReadingLink()}
      className="block h-full text-left no-underline group"
    >
      <div className="flex flex-col h-full overflow-hidden bg-white rounded-lg shadow-md transition-shadow duration-300 ease-in-out group-hover:shadow-xl">
        <div className="relative w-full aspect-[3/5] bg-slate-200">
          <img
            ref={imgRef}
            alt={bookLabel}
            src={
              isIntersected
                ? imageSrc
                : 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
            }
            title={bookLabel}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        <div className="flex flex-col flex-grow p-3">
          <p
            className="flex-grow font-semibold text-gray-800 text-sm truncate group-hover:whitespace-normal"
            title={bookLabel}
          >
            {bookLabel}
          </p>

          {user.username && (
            <div
              className="flex items-center justify-between mt-2"
              // FIX: This onClick handler is the key.
              onClick={(e) => {
                // 1. Prevent the parent <Link> from navigating.
                e.preventDefault();

                // 2. Manually call the toggle function.
                onToggle({
                  link,
                  startPage,
                  pageNo: startPage,
                  endPage,
                  updatedAt: serverTimestamp(),
                });
              }}
            >
              <label className="flex items-center cursor-pointer space-x-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isReading}
                    // The input is now just for accessibility; the logic is in the parent div.
                    readOnly
                  />
                  <div
                    className={`block w-10 h-6 rounded-full transition-colors ${isReading ? 'bg-green-500' : 'bg-gray-300'}`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                      isReading ? 'translate-x-full' : 'translate-x-0'
                    }`}
                  ></div>
                </div>
                <span className="text-xs text-gray-600 select-none">
                  Reading
                </span>
              </label>

              {currentlyReadingBook && (
                <p className="text-xs font-medium text-blue-600">
                  {calculateProgress()}%
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default BookCoverPage;
